import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetCompanyQuery,
  useCreateOwnershipDraftFetchMutation,
  useUpdateOwnershipIndividualMutation,
  useUpdateOwnershipCompanyDetailsMutation,
  useDeleteOwnershipIndDraftMutation,
  useDeleteOwnershipCompDraftMutation,
} from "../../../../store/Company/companyApiSlice";
import { setCurrentEditingCompany } from "../../../../store/Company/companySlice";
import { CommonToast } from "../../../common/Toast";
import {
  draftTotal,
  liveOwners,
  liveTotal,
  mergeOwnershipDrafts,
  ownerOf,
} from "./ownershipFields";

/* The Ownership Structure section's data. */
export const useOwnershipStructure = ({ onStatusChange } = {}) => {
  const dispatch = useDispatch();
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  /* The draft RESPONSE, not the merged rows. */
  const [draftResponse, setDraftResponse] = useState(null);
  const [fetchDrafts] = useCreateOwnershipDraftFetchMutation();
  const [updateIndividual] = useUpdateOwnershipIndividualMutation();
  const [updateCompanyDetails] = useUpdateOwnershipCompanyDetailsMutation();
  const [deleteIndDraft] = useDeleteOwnershipIndDraftMutation();
  const [deleteCompDraft] = useDeleteOwnershipCompDraftMutation();

  const { data, refetch } = useGetCompanyQuery({ companyId: companyID, token });

  useEffect(() => {
    if (data) dispatch(setCurrentEditingCompany(data));
  }, [data, dispatch]);

  /* Which company the newest in-flight drafts request is for. */
  const latestRequest = useRef(null);

  const loadDrafts = useCallback(async () => {
    if (!companyID) return;
    latestRequest.current = companyID;
    try {
      const res = await fetchDrafts({ companyID, token });
      if (latestRequest.current !== companyID) return;
      setDraftResponse(res?.data ?? null);
    } catch {
      /* The live call is unguarded, so a failure became an unhandled
         rejection. The section falls back to the live owners, which is the
         safe reading: it shows what is on the record. */
      if (latestRequest.current !== companyID) return;
      setDraftResponse(null);
    }
  }, [companyID, token, fetchDrafts]);

  /* Keyed on the company ID — a string — rather than on the company object.
     The object is replaced on every answer from the query above, which is what
     made this run repeatedly; the id changes only when the company does. */
  useEffect(() => {
    if (!companyID) return;
    loadDrafts();
  }, [companyID, loadDrafts]);

  /* Merged against the company as it is NOW, so the rows cannot disagree with
     the record the rest of the section is reading. */
  const draftRows = useMemo(
    () => mergeOwnershipDrafts(draftResponse, currentEditingCompany),
    [draftResponse, currentEditingCompany]
  );

  /* Transcribed: when drafts exist they REPLACE the live list, because
     mergeOwnershipDrafts has already folded in every undrafted owner. */
  const usingDrafts = draftRows.length > 0;
  const rows = usingDrafts ? draftRows : liveOwners(currentEditingCompany).map(ownerOf);

  /* Transcribed: the total follows whichever list is showing. */
  const total = usingDrafts ? draftTotal(draftRows) : liveTotal(currentEditingCompany);

  const refresh = () => {
    /* refetch() returns a promise. Left bare, a rejected one becomes an
       unhandled rejection and CRA's dev overlay reports it as a bare
       "[object Object]" with no clue where it came from. */
    Promise.resolve(refetch()).catch(() => {});
    loadDrafts();
  };

  /* Transcribed delete, four ways: a draft row is deleted outright, a live one is soft-deleted with is_deleted: 1. */
  const remove = async (record) => {
    try {
      if (record?.ownership_ind_draftID) {
        const res = await deleteIndDraft({ data: record.ownership_ind_draftID });
        if (res?.data == null) {
          CommonToast.success("Ownership Deleted Successfully");
          refresh();
        }
        return;
      }
      if (record?.ownership_comp_draftID) {
        const res = await deleteCompDraft({ data: record.ownership_comp_draftID });
        if (res?.data == null) {
          CommonToast.success("Ownership Deleted Successfully");
          refresh();
        }
        return;
      }
      if (record?.ownership_indID) {
        const res = await updateIndividual({
          data: { is_deleted: 1 },
          ownership_indId: record.ownership_indID,
          token,
        }).unwrap();
        if (Number(res?.is_deleted) === 1) {
          onStatusChange?.();
          CommonToast.success("Ownership Deleted Successfully");
          refresh();
        }
        return;
      }
      if (record?.ownership_compID) {
        const res = await updateCompanyDetails({
          data: {
            is_deleted: 1,
            /* Load-bearing, and the reason this delete used to 403. */
            current_companyID: companyID,
          },
          ownership_compId: record.ownership_compID,
          token,
        }).unwrap();
        if (Number(res?.is_deleted) === 1) {
          onStatusChange?.();
          CommonToast.success("Ownership Deleted Successfully");
          refresh();
        }
      }
    } catch (error) {
      /* The live handler has no catch at all on the two unwrapped calls. */
      /* DRF's permission denial arrives as `data.detail`; other endpoints on
         this flow use `data.message`. Both are read before the fallback, so a
         refusal says why instead of showing a bare object. */
      CommonToast.error(
        error?.data?.detail ||
          error?.data?.message ||
          error?.message ||
          "Couldn't delete this owner."
      );
    }
  };

  return {
    rows,
    usingDrafts,
    total,
    remove,
    refresh,
    companyStatus: currentEditingCompany?.company_status,
  };
};

export default useOwnershipStructure;
